import { useCallback, useEffect, useState } from "react";

import { ArrowLeftOutlined, DownloadOutlined } from "@ant-design/icons";
import {
  Alert,
  Badge,
  Button,
  Drawer,
  Dropdown,
  Grid,
  Modal,
  Skeleton,
  Space,
  Switch,
  Tabs,
  Tooltip,
  Typography,
  message,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";

import { Documento } from "../../components/document/Documento";
import { descargarDocumento, ErrorApi, getDocumento, HAY_DESCARGA } from "../../lib/api/client";
import type { CVDocument } from "../../lib/document/model";
import type { Ruta } from "../../lib/document/paths";
import { rutasMarcadas } from "../../lib/suggestions/types";
import { EnviarModal } from "../export/EnviarModal";
import { PanelCambios } from "../review/PanelCambios";
import { useSugerencias } from "../review/useSugerencias";
import { PanelEdicion } from "./PanelEdicion";
import { useAutoguardado } from "./useAutoguardado";
import { useDocumento } from "./useDocumento";

import "./editor.css";

const { Text } = Typography;
const { useBreakpoint } = Grid;

const Indicador = ({ estado }: { estado: ReturnType<typeof useAutoguardado>["estado"] }) => {
  // El texto dice lo que pasa de verdad. "Guardado" cuando no se ha guardado
  // nada es peor que no poner nada: la persona confia y cierra la pestaña.
  switch (estado.fase) {
    case "pendiente":
      return <Text type="secondary">Cambios sin guardar…</Text>;
    case "guardando":
      return <Text type="secondary">Guardando…</Text>;
    case "error":
      return (
        <Text type="danger">
          {estado.mensaje}
          {estado.reintentable ? "" : " Tus cambios siguen en pantalla."}
        </Text>
      );
    case "limpio":
      // Se guardó todo MENOS la HV inclusiva: hay que decirlo, porque en
      // pantalla el símbolo sí aparece y la persona cree que quedó guardado.
      if (estado.omitidos?.includes("is_inclusive")) {
        return (
          <Tooltip title="El servidor todavía no tiene el campo de HV inclusiva. El resto de tu hoja de vida sí se guardó.">
            <Text type="warning">Guardado, salvo la HV inclusiva</Text>
          </Tooltip>
        );
      }
      return estado.ultimo ? (
        <Text type="success">
          Guardado{" "}
          {estado.ultimo.toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" })}
        </Text>
      ) : (
        <Text type="secondary">Todo al día</Text>
      );
  }
};

export const EditorPage = () => {
  const navegar = useNavigate();
  const [modal, contextoModal] = Modal.useModal();
  const [aviso, contextoAviso] = message.useMessage();
  const [descargando, setDescargando] = useState(false);
  const { cvId } = useParams();
  const id = Number(cvId);
  const pantallas = useBreakpoint();
  const esMovil = !pantallas.lg;

  const { doc, revision, despachar, editar, recuperarRespaldo } = useDocumento(id);
  const [seleccionada, setSeleccionada] = useState<Ruta | null>(null);
  const [pestana, setPestana] = useState<"cambios" | "editar">("cambios");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [desdeRespaldo, setDesdeRespaldo] = useState(false);

  // Al terminar un guardado se adoptan los ids reales que asignó el servidor,
  // sin pisar lo que el usuario haya escrito mientras tanto: solo interesa que
  // las filas nuevas dejen de tener id negativo.
  const onGuardado = useCallback(
    (guardado: CVDocument) => despachar({ tipo: "ids:adoptar", guardado }),
    [despachar],
  );
  const { estado } = useAutoguardado(id, doc, revision, onGuardado);

  const sug = useSugerencias(doc, editar);
  const marcadas = rutasMarcadas(sug.sugerencias);

  // Al pulsar un bloque del documento el panel salta a "Editar", que es lo que
  // la persona venia a hacer. Si ese bloque tiene una sugerencia sin revisar,
  // manda la revision: primero se decide sobre el cambio propuesto.
  const seleccionar = useCallback(
    (ruta: Ruta) => {
      setSeleccionada(ruta);
      setPestana(marcadas.has(ruta) ? "cambios" : "editar");
    },
    [marcadas],
  );

  useEffect(() => {
    if (!Number.isFinite(id) || id <= 0) {
      setError("El identificador de la hoja de vida no es válido.");
      return;
    }

    let vigente = true;
    setError(null);
    setDesdeRespaldo(false);

    getDocumento(id)
      .then((d) => vigente && despachar({ tipo: "cargado", doc: d }))
      .catch((e: unknown) => {
        if (!vigente) return;
        const respaldo = recuperarRespaldo();
        if (respaldo) {
          despachar({ tipo: "cargado", doc: respaldo });
          setDesdeRespaldo(true);
          return;
        }
        setError(
          e instanceof ErrorApi && e.status === 0
            ? "No hay conexión con el servidor. Revisa tu red e inténtalo de nuevo."
            : e instanceof ErrorApi
              ? e.message
              : "No pudimos cargar tu hoja de vida.",
        );
      });

    return () => {
      vigente = false;
    };
  }, [id, despachar, recuperarRespaldo]);

  const pendientesSinRevisar = sug.sugerencias.filter((s) => s.estado === "pendiente").length;

  const panel = doc && (
    <Tabs
      activeKey={pestana}
      onChange={(k) => setPestana(k as "cambios" | "editar")}
      items={[
        {
          key: "cambios",
          label: (
            <Badge count={pendientesSinRevisar} size="small" offset={[8, 0]}>
              <span>Cambios</span>
            </Badge>
          ),
          children: (
            <PanelCambios
              sugerencias={sug.sugerencias}
              seleccionada={seleccionada}
              cargando={sug.cargando}
              aviso={sug.aviso}
              onSeleccionar={setSeleccionada}
              onAceptar={sug.aceptar}
              onRechazar={sug.rechazar}
              onEditar={sug.editar}
              onAceptarRestantes={sug.aceptarRestantes}
            />
          ),
        },
        {
          key: "editar",
          label: "Editar",
          children: (
            <PanelEdicion
              doc={doc}
              ruta={seleccionada}
              editar={editar}
              despachar={despachar}
              onCerrar={() => setSeleccionada(null)}
              onIr={setSeleccionada}
            />
          ),
        },
      ]}
    />
  );

  /**
   * Volver a la subida, para cargar otra hoja de vida o empezar de cero.
   *
   * Solo se pregunta si hay algo sin guardar: con autoguardado, lo normal es
   * que todo esté ya en el servidor, y un "¿seguro?" de rutina enseña a la gente
   * a pulsar "sí" sin leer justo el día que importa.
   */
  const volver = () => {
    const sinGuardar = estado.fase === "pendiente" || estado.fase === "guardando";
    if (!sinGuardar) {
      navegar("/");
      return;
    }
    modal.confirm({
      title: "Aún se están guardando tus últimos cambios",
      content: "Si sales ahora podrías perder lo que acabas de escribir.",
      okText: "Salir igualmente",
      cancelText: "Quedarme",
      onOk: () => navegar("/"),
    });
  };

  /**
   * Descarga directa del PDF o el Word, hoy solo en desarrollo (VITE_DOCS_URL):
   * sirve para comprobar que el archivo es la misma hoja que se ve en pantalla.
   * Genera desde el documento en memoria, así que refleja incluso lo que aún no
   * terminó de guardarse.
   */
  const descargar = async (formato: "pdf" | "docx") => {
    if (!doc) return;
    setDescargando(true);
    try {
      await descargarDocumento(doc, formato);
    } catch (e) {
      aviso.error(e instanceof ErrorApi ? e.message : "No pudimos generar el documento.");
    } finally {
      setDescargando(false);
    }
  };

  const alternarInclusiva = (activa: boolean) => {
    editar("is_inclusive", activa);
    // Al activarla se abre el contexto de inclusión, donde están el tipo y las
    // preguntas opcionales: así se ve que existen sin obligar a rellenarlas.
    if (activa) {
      setSeleccionada("disability_details");
      setPestana("editar");
    }
  };

  return (
    <div className="editor">
      {contextoModal}
      {contextoAviso}
      <header className="editor-barra">
        <div className="editor-barra-izq">
          <Tooltip title="Subir otra hoja de vida o empezar de cero">
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={volver}>
              Volver
            </Button>
          </Tooltip>
          <img src="/logo-almia.svg" alt="Almia" height={26} />
        </div>
        <Space size={14}>
          <Tooltip title="Añade el símbolo de inclusión y una sección de contexto de inclusión a tu hoja de vida. Detallar la discapacidad es opcional.">
            <label className="editor-inclusiva">
              <Switch
                size="small"
                checked={Boolean(doc?.is_inclusive)}
                disabled={!doc}
                onChange={alternarInclusiva}
              />
              <span>HV inclusiva</span>
            </label>
          </Tooltip>
          <Indicador estado={estado} />
          {HAY_DESCARGA && (
            <Dropdown
              disabled={!doc}
              menu={{
                items: [
                  { key: "pdf", label: "PDF" },
                  { key: "docx", label: "Word" },
                ],
                onClick: ({ key }) => void descargar(key as "pdf" | "docx"),
              }}
            >
              <Button icon={<DownloadOutlined />} loading={descargando}>
                Descargar
              </Button>
            </Dropdown>
          )}
          <Button
            type="primary"
            disabled={!doc || estado.fase !== "limpio"}
            onClick={() => setEnviando(true)}
          >
            Recibir mi hoja de vida
          </Button>
        </Space>
      </header>

      <div className="editor-cuerpo">
        <main className="editor-documento">
          {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
          {desdeRespaldo && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Estás viendo una copia local"
              description="No pudimos contactar al servidor, así que recuperamos los últimos cambios de este navegador. Reintenta antes de descargar."
            />
          )}

          {!doc && !error && (
            <div className="doc-hoja">
              <Skeleton active title={{ width: "45%" }} paragraph={{ rows: 2 }} />
              <div style={{ height: 24 }} />
              <Skeleton active title={{ width: "30%" }} paragraph={{ rows: 4 }} />
            </div>
          )}

          {doc && (
            <>
              <Documento
                doc={doc}
                seleccionada={seleccionada}
                onSeleccionar={seleccionar}
                conSugerencia={marcadas}
              />
              <div className="editor-añadir">
                <Button size="small" onClick={() => despachar({ tipo: "experiencia:añadir" })}>
                  Añadir experiencia
                </Button>
                <Button size="small" onClick={() => despachar({ tipo: "estudio:añadir" })}>
                  Añadir estudio
                </Button>
                <Button size="small" onClick={() => despachar({ tipo: "otro:añadir" })}>
                  Añadir sección
                </Button>
              </div>
            </>
          )}
        </main>

        {/* En escritorio el panel vive al lado y no tapa el documento: hay que
            poder leer el texto mientras se edita. En móvil no cabe, así que
            sube como hoja inferior solo cuando hay algo seleccionado. */}
        {!esMovil && <aside className="editor-panel">{panel}</aside>}
      </div>

      {doc && (
        <EnviarModal doc={doc} abierto={enviando} onCerrar={() => setEnviando(false)} />
      )}

      {esMovil && (
        <Drawer
          open={Boolean(seleccionada)}
          placement="bottom"
          height="70vh"
          onClose={() => setSeleccionada(null)}
          styles={{ body: { padding: 16 } }}
        >
          {panel}
        </Drawer>
      )}
    </div>
  );
};
